import { progressStateRepository, vacationAllowanceRepository } from '../db/database';
import { REWARDS, type ProgressState, type Reward } from '../models/types';
import { todayIso } from '../utils/date';
import { getOrCreateProgressState } from './dailyRollover';
import { isDayFrozen } from './dayCover';
import { applyDeltaToBar } from './scoring';

/**
 * Ödüller — CLAUDE.md §7.6.
 *
 * Şu an tek bir ödül var: 100 Başarı Puanı karşılığında 1 Day Streak
 * günü. Puan, ayrı bir "kazanılmış puan" havuzundan değil, doğrudan
 * Başarı Barı'nın kendisinden düşer.
 */

export interface RewardView {
  reward: Reward;
  /** Kullanıcının şu an harcayabileceği puan. */
  spendablePoints: number;
  /** Bu ödül için puan yetiyor mu? */
  affordable: boolean;
}

/**
 * Harcanabilir puan = **kapanmış günün** bar değeri (§7.6).
 *
 * Bugünün henüz kesinleşmemiş kazancı DAHİL DEĞİLDİR: gün sonunda
 * yapılmamış alışkanlıkların cezası da eklenecek, yani bugünkü sayı
 * hâlâ düşebilir. Üzerine harcama yapmak hesabı tutarsız kılardı.
 * Bu yüzden Anasayfa'daki sayı ile buradaki sayı farklı olabilir.
 */
export function getSpendablePoints(progress: ProgressState): number {
  return progress.barValue;
}

/** Ödül listesini, her birinin alınabilir olup olmadığıyla döndürür. */
export async function getRewardViews(): Promise<RewardView[]> {
  const progress = await getOrCreateProgressState();
  const spendablePoints = getSpendablePoints(progress);

  return REWARDS.map((reward) => ({
    reward,
    spendablePoints,
    affordable: spendablePoints >= reward.costInScore,
  }));
}

/** Ödül kullanma sonucu — ekranın ne göstereceğini belirler. */
export type RedeemResult =
  | { ok: true; newBarValue: number }
  | { ok: false; reason: 'insufficient' | 'alreadyCovered' };

/**
 * Ödülü kullanır: 100 puan karşılığında BUGÜNÜ satın alır.
 *
 * **KESİNLEŞTİ (§7.6) — Ödül, sayaca körlemesine +1 eklemez; belirli
 * bir günü dondurur.** Satın alınan gün tatil günüyle aynı şekilde
 * davranır: o gün ceza işlemez ve gün Day Streak'e sayılır.
 *
 * Neden böyle: eskiden ödül yalnızca sayacı artırıyordu, ama o gün
 * alışkanlıklar yapılmayınca gün kapanışında seri sıfırlanıyor ve
 * satın alınan gün BOŞA GİDİYORDU. Günü işaretlemek bu çelişkiyi
 * ortadan kaldırıyor.
 *
 * Sayaç burada artırılmaz — gün kapanışında artar. Doğal tamamlanan
 * bir günde de böyle olur (bugün alışkanlıklarını yapman sayacı yarın
 * artırır), dolayısıyla davranış tutarlı kalır ve aynı gün iki kez
 * sayılma riski oluşmaz.
 *
 * Puan düşüşü challenge iptalindeki gibi DOĞRUDAN ve ANINDA uygulanır
 * — günlük alışkanlık hesabından (§7.2) bağımsızdır ve `Math.trunc()`
 * kuralına tabi değildir. `applyDeltaToBar` sayesinde bar 0'ın altına
 * inmez.
 */
export async function redeemReward(reward: Reward): Promise<RedeemResult> {
  const [progress, allowance] = await Promise.all([
    getOrCreateProgressState(),
    vacationAllowanceRepository.get(),
  ]);
  const today = todayIso();

  // §7.6 — Gün zaten güvencedeyse (tatil ya da daha önceki bir ödül)
  // ikinci kez harcama yapılmaz; 100 puan karşılıksız yanardı.
  if (isDayFrozen(today, allowance, progress)) {
    return { ok: false, reason: 'alreadyCovered' };
  }

  // Yeniden kontrol: ekran ile veritabanı arasında geçen sürede puan
  // değişmiş olabilir (örn. challenge iptali). Butonun pasif olması
  // tek başına yeterli bir güvence değil.
  if (getSpendablePoints(progress) < reward.costInScore) {
    return { ok: false, reason: 'insufficient' };
  }

  const newBarValue = applyDeltaToBar(progress.barValue, -reward.costInScore);

  await progressStateRepository.upsert({
    ...progress,
    barValue: newBarValue,
    rewardDates: [...progress.rewardDates, today].sort(),
  });

  return { ok: true, newBarValue };
}
